/**
 * ⟦C15 · A-135 · A-136⟧ **Une icône PNG 8-bit qui ne casse jamais un écran.**
 *
 * Sur les 160 assets produits, plusieurs familles n'ont pas de fichier pour CHAQUE id du
 * contenu (39 des 42 `skills.json`, 17 des 25 `boutique.json`, 30 des 140 `events.json` —
 * rapporté au complet dans l'avenant de constat de ce chantier). Le chantier le dit
 * explicitement : « une image manquante ne doit JAMAIS casser l'écran ». Plutôt qu'une liste
 * d'exceptions à tenir à jour à la main (un id de plus dans le contenu, un asset de plus
 * produit plus tard), ce composant essaie l'image et replie sur le contenu existant
 * (emoji, cadre neutre…) au premier `onError` — le navigateur sait déjà dire « ce fichier
 * n'existe pas », pas la peine de le redire dans une table.
 *
 * `image-rendering: pixelated` est posé une fois pour toutes via `.pixel-asset` (`app.css`) —
 * la même règle que `.chambre-scene` et `.avatar-chambre img` (⟦C14 critère 2⟧, ⟦C15⟧).
 */

import { useState, type ReactNode } from "react";

export function IconeAsset({
  src,
  alt = "",
  repli = null,
  className,
  loadingLazy = true,
}: {
  src: string;
  alt?: string;
  /** Ce qui s'affiche si l'image ne charge pas (404, asset non produit) — emoji ou `null`. */
  repli?: ReactNode;
  className?: string;
  /** `loading="lazy"` par défaut (⟦C15 piège⟧ : ne pas charger 160 images au boot) — désactiver
      pour un élément toujours visible dès le montage de son écran (ex. la seule illustration
      d'une modale déjà ouverte). */
  loadingLazy?: boolean;
}) {
  const [enErreur, setEnErreur] = useState(false);
  if (enErreur) return <>{repli}</>;
  return (
    <img
      src={src}
      alt={alt}
      aria-hidden={alt === "" ? true : undefined}
      className={className ? `pixel-asset ${className}` : "pixel-asset"}
      loading={loadingLazy ? "lazy" : undefined}
      onError={() => setEnErreur(true)}
    />
  );
}
